import { forwardRef, type ReactNode } from "react";

// eslint-disable-next-line @typescript-eslint/no-restricted-imports, no-restricted-imports
import { Card } from "antd";
import { clsx } from "clsx";

import { RV_CARD_SPACING, RV_CARD_VARIANT, type RVCardSpacing, type RVCardVariant } from "./RVCardTypes";

import type { CardProps } from "antd";

import styles from "./RVCard.module.scss";

export type RVCardProps = CardProps & {
    /** Additional CSS class names to apply to the card */
    className?: string;
    /** Content rendered inside the card body */
    children?: ReactNode;
    /** When true, renders a loading skeleton in place of the card body */
    loading?: boolean;
    /**
     * Padding inside the card body, and gap between flex children (when `bodyFlex` is true).
     * `"tight"` = 8px (default), `"wide"` = 12px.
     */
    spacing?: RVCardSpacing;
    /** When true, sets the card body to `display:flex; flex-direction:column`. */
    bodyFlex?: boolean;
    /** When true, sets `height:100%` on the card body. */
    bodyFullHeight?: boolean;
    /**
     * Visual style of the card surface.
     * - `"raised"` (default) — drop shadow + border.
     * - `"flat"` — border only, no shadow.
     */
    variant?: RVCardVariant;
};

/**
 * Regal Voice themed wrapper around Ant Design's `Card` that centralises
 * surface styling (padding, borders, shadows) via the module stylesheet while
 * preserving the familiar `Card` API for callers.
 *
 * @example
 * // Basic usage — default 16px body padding
 * <RVCard>Content here</RVCard>
 *
 * // Flex column body that fills height, wider spacing
 * <RVCard bodyFlex bodyFullHeight spacing="wide">Content here</RVCard>
 *
 * // Flat (no shadow) surface
 * <RVCard variant="flat">Content here</RVCard>
 */
export const RVCard = forwardRef<HTMLDivElement, RVCardProps>(function RVCard(
    {
        className,
        children,
        loading = false,
        spacing = RV_CARD_SPACING.default,
        bodyFlex,
        bodyFullHeight,
        bodyStyle: callerBodyStyle,
        variant = RV_CARD_VARIANT.raised,
        ...rest
    },
    ref
) {
    return (
        <Card
            {...rest}
            ref={ref}
            loading={loading}
            className={clsx(styles.rvCard, className, {
                [styles.flat]: variant === RV_CARD_VARIANT.flat,
                [styles.spacingTight]: spacing === RV_CARD_SPACING.tight,
                [styles.spacingWide]: spacing === RV_CARD_SPACING.wide,
                [styles.spacingExtraWide]: spacing === RV_CARD_SPACING.extraWide,
                [styles.bodyFlex]: bodyFlex,
                [styles.bodyFullHeight]: bodyFullHeight,
            })}
            bodyStyle={callerBodyStyle}
        >
            {children}
        </Card>
    );
});
